import { useCallback, useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "wheel",
];

export interface UseIdleOptions {
  /** Idle threshold in milliseconds. */
  timeoutMs: number;
  /** Whether the detector is active. Defaults to true. */
  enabled?: boolean;
}

export interface UseIdleResult {
  idle: boolean;
  /** Manually mark activity (e.g. when dismissing the takeover). */
  reset: () => void;
  /** Force idle immediately (e.g. a "preview" button). */
  forceIdle: () => void;
}

/** Fires `idle` after `timeoutMs` without user interaction, and resets to
 *  active on any interaction. Used to drive the screensaver takeover. */
export function useIdle({ timeoutMs, enabled = true }: UseIdleOptions): UseIdleResult {
  const [idle, setIdle] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const schedule = useCallback(() => {
    clear();
    timer.current = setTimeout(() => setIdle(true), timeoutMs);
  }, [clear, timeoutMs]);

  const reset = useCallback(() => {
    setIdle(false);
    if (enabled) schedule();
  }, [enabled, schedule]);

  const forceIdle = useCallback(() => {
    clear();
    setIdle(true);
  }, [clear]);

  useEffect(() => {
    if (!enabled) {
      clear();
      setIdle(false);
      return;
    }
    schedule();
    const onActivity = () => {
      // Only reset the timer; flipping back to active happens here too.
      setIdle(false);
      schedule();
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    return () => {
      clear();
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
    };
  }, [enabled, schedule, clear]);

  return { idle, reset, forceIdle };
}
